import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { toast } from 'sonner';
import { generateNewBlock } from '@/pages/create-report/utils/generateNewBlock';
import { TestBlock } from '@/types/report-payload';
import { findBlockElement } from '../pagination/utils';
import { ReportData } from '../types';
import {
    BLOCK_CLIPBOARD_MIME,
    BlockClipboardPayload,
    InsertTarget,
    collectBlocksForClipboard,
    getBlockRealId,
    insertClipboardBlocks,
    moveBlocksToIndex,
    reindexBlocks,
    removeBlocksWithCascade,
    resolveInsertionIndex,
    serializeBlockClipboard,
} from './blockClipboard';
import { useBlockClipboardEvents } from './useBlockClipboardEvents';

export type BlockSelectionMode = 'replace' | 'toggle' | 'range';

export interface BlockClipboardState {
    mode: 'copy' | 'cut';
    count: number;
}

export interface TestBlockSelectionApi {
    enabled: boolean;
    selectedIds: ReadonlySet<string>;
    isSelected: (realId: string) => boolean;
    select: (realId: string, mode: BlockSelectionMode) => void;
    /** Replaces the whole selection at once (marquee). Callers wanting a
     *  union pass the pre-merged set. */
    replaceSelection: (realIds: string[]) => void;
    clear: () => void;
    copy: (ids?: string[]) => void;
    cut: (ids?: string[]) => void;
    duplicate: (ids?: string[]) => void;
    deleteBlocks: (ids?: string[]) => void;
    pasteAt: (target: InsertTarget) => void;
    /** Moves existing blocks (a table carries its linked companions) to
     *  `target`, preserving ids. No-op when the order would not change. */
    moveBlocks: (realIds: string[], target: InsertTarget) => void;
    /** Creates a fresh block of `toolId` at `target` and returns its id. Charts
     *  are not handled here — they go through the chart config modal, which
     *  places them next to their source table. */
    insertBlockAt: (toolId: string, target: InsertTarget) => string | null;
    /** Inserts already-built blocks at `target` and returns their ids. Used by
     *  the paste router for blocks materialized from external clipboard
     *  content; unlike `pasteAt` it leaves the block clipboard untouched. */
    insertBlocksAt: (newBlocks: TestBlock[], target: InsertTarget) => string[];
    /** Plain-text fingerprint written to the system clipboard at copy/cut time.
     *  Callers compare it against the live clipboard before preferring the
     *  carried blocks — the same trust rule the keyboard path applies. */
    getBlockClipboardFingerprint: () => string | null;
    clipboardState: BlockClipboardState | null;
    dismissClipboard: () => void;
}

const noop = () => {};

const INERT_API: TestBlockSelectionApi = {
    enabled: false,
    selectedIds: new Set(),
    isSelected: () => false,
    select: noop,
    replaceSelection: noop,
    clear: noop,
    copy: noop,
    cut: noop,
    duplicate: noop,
    deleteBlocks: noop,
    pasteAt: noop,
    moveBlocks: noop,
    insertBlockAt: () => null,
    insertBlocksAt: () => [],
    getBlockClipboardFingerprint: () => null,
    clipboardState: null,
    dismissClipboard: noop,
};

const TestBlockSelectionContext = createContext<TestBlockSelectionApi>(INERT_API);

// eslint-disable-next-line react-refresh/only-export-components
export const useTestBlockSelection = () => useContext(TestBlockSelectionContext);

/** Elements marked with this attribute never clear the selection when clicked. */
export const BLOCK_SELECTION_UI_ATTR = 'data-block-selection-ui';

interface InternalClipboard {
    payload: BlockClipboardPayload;
    /** text/plain written alongside the payload; used to confirm on paste
     *  that the system clipboard was not overwritten externally. */
    textFingerprint: string;
}

interface TestBlockSelectionProviderProps {
    blocks: TestBlock[];
    onUpdate: (updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
    disabled?: boolean;
    reportId?: string;
    children: ReactNode;
}

const scrollToBlock = (blockId: string) => {
    setTimeout(() => {
        const previewContainer = document.querySelector('[data-preview-container]');
        if (!previewContainer) return;

        const blockEl = findBlockElement(previewContainer, blockId);
        blockEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
};

const pluralize = (count: number, singular: string, plural: string) =>
    `${count} ${count === 1 ? singular : plural}`;

export function TestBlockSelectionProvider({
    blocks,
    onUpdate,
    disabled = false,
    reportId,
    children,
}: TestBlockSelectionProviderProps) {
    const enabled = !disabled;

    const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
    const [clipboardState, setClipboardState] = useState<BlockClipboardState | null>(null);

    const anchorIdRef = useRef<string | null>(null);
    const internalClipboardRef = useRef<InternalClipboard | null>(null);

    const blocksRef = useRef(blocks);
    blocksRef.current = blocks;
    const onUpdateRef = useRef(onUpdate);
    onUpdateRef.current = onUpdate;
    const reportIdRef = useRef(reportId);
    reportIdRef.current = reportId;
    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;
    const clipboardStateRef = useRef(clipboardState);
    clipboardStateRef.current = clipboardState;

    // Prune selections that no longer resolve to a block (undo, delete, cut)
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const existing = new Set(blocks.map(getBlockRealId));
            const pruned = new Set([...prev].filter((id) => existing.has(id)));
            return pruned.size === prev.size ? prev : pruned;
        });
    }, [blocks]);

    const clear = useCallback(() => {
        anchorIdRef.current = null;
        setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
    }, []);

    const select = useCallback(
        (realId: string, mode: BlockSelectionMode) => {
            if (!enabled) return;

            setSelectedIds((prev) => {
                if (mode === 'toggle') {
                    const next = new Set(prev);
                    if (next.has(realId)) {
                        next.delete(realId);
                    } else {
                        next.add(realId);
                    }
                    anchorIdRef.current = realId;
                    return next;
                }

                if (mode === 'range') {
                    const order = blocksRef.current.map(getBlockRealId);
                    const anchorIndex = order.indexOf(anchorIdRef.current || realId);
                    const targetIndex = order.indexOf(realId);
                    if (anchorIndex === -1 || targetIndex === -1) {
                        anchorIdRef.current = realId;
                        return new Set([realId]);
                    }
                    const [from, to] =
                        anchorIndex <= targetIndex
                            ? [anchorIndex, targetIndex]
                            : [targetIndex, anchorIndex];
                    return new Set(order.slice(from, to + 1));
                }

                // replace: clicking the sole selected block deselects it
                if (prev.size === 1 && prev.has(realId)) {
                    anchorIdRef.current = null;
                    return new Set();
                }
                anchorIdRef.current = realId;
                return new Set([realId]);
            });
        },
        [enabled],
    );

    const isSelected = useCallback((realId: string) => selectedIds.has(realId), [selectedIds]);

    const replaceSelection = useCallback(
        (realIds: string[]) => {
            if (!enabled) return;

            setSelectedIds((prev) => {
                if (prev.size === realIds.length && realIds.every((id) => prev.has(id))) {
                    return prev;
                }
                return new Set(realIds);
            });
            if (realIds.length > 0) {
                anchorIdRef.current = realIds[realIds.length - 1];
            }
        },
        [enabled],
    );

    const resolveIds = useCallback((ids?: string[]) => {
        const resolved = ids && ids.length > 0 ? ids : [...selectedIdsRef.current];
        return resolved;
    }, []);

    /** Collects the family, stashes it on the internal clipboard, flips carry mode. */
    const buildClipboard = useCallback(
        (mode: 'copy' | 'cut', ids?: string[]) => {
            const targetIds = resolveIds(ids);
            if (targetIds.length === 0) return null;

            const collected = collectBlocksForClipboard(blocksRef.current, targetIds);
            if (collected.length === 0) return null;

            const payload: BlockClipboardPayload = {
                version: 1,
                mode,
                reportId: reportIdRef.current,
                blocks: collected,
            };
            const serialized = serializeBlockClipboard(payload);
            internalClipboardRef.current = { payload, textFingerprint: serialized.text };
            setClipboardState({ mode, count: collected.length });

            return { payload, serialized, targetIds };
        },
        [resolveIds],
    );

    const removeBlocks = useCallback((realIds: string[]) => {
        onUpdateRef.current((prev) => {
            const currentTests = prev.tests || { section_title: '', blocks: [] };
            return {
                tests: {
                    ...currentTests,
                    blocks: removeBlocksWithCascade(currentTests.blocks || [], realIds),
                },
            };
        });
    }, []);

    const applyInsert = useCallback(
        (
            payload: BlockClipboardPayload,
            target: InsertTarget,
            { consumeClipboard = true }: { consumeClipboard?: boolean } = {},
        ) => {
            let insertedIds: string[] = [];
            onUpdateRef.current((prev) => {
                const currentTests = prev.tests || { section_title: '', blocks: [] };
                const result = insertClipboardBlocks(currentTests.blocks || [], payload, target);
                insertedIds = result.insertedIds;
                return { tests: { ...currentTests, blocks: result.blocks } };
            });

            if (insertedIds.length === 0) return insertedIds;

            setSelectedIds(new Set(insertedIds));
            anchorIdRef.current = insertedIds[insertedIds.length - 1];
            // Only an in-app paste spends the carried blocks. Pasting external
            // content must leave whatever the user copied still available.
            if (consumeClipboard) setClipboardState(null);
            scrollToBlock(insertedIds[0]);
            toast.success(`${pluralize(insertedIds.length, 'bloco colado', 'blocos colados')}.`);

            return insertedIds;
        },
        [],
    );

    const moveBlocks = useCallback((realIds: string[], target: InsertTarget) => {
        if (realIds.length === 0) return;

        onUpdateRef.current((prev) => {
            const currentTests = prev.tests || { section_title: '', blocks: [] };
            const blocks = currentTests.blocks || [];
            const next = moveBlocksToIndex(blocks, realIds, target);
            // Same-reference means the move was a no-op; skip the write so undo
            // history stays clean.
            if (next === blocks) return {};
            return { tests: { ...currentTests, blocks: next } };
        });
    }, []);

    const insertBlocksAt = useCallback(
        (newBlocks: TestBlock[], target: InsertTarget) => {
            if (newBlocks.length === 0) return [];
            return applyInsert(
                {
                    version: 1,
                    mode: 'copy',
                    reportId: reportIdRef.current,
                    blocks: newBlocks,
                },
                target,
                { consumeClipboard: false },
            );
        },
        [applyInsert],
    );

    const insertBlockAt = useCallback((toolId: string, target: InsertTarget) => {
        let insertedId: string | null = null;

        onUpdateRef.current((prev) => {
            const currentTests = prev.tests || { section_title: '', blocks: [] };
            const blocks = currentTests.blocks || [];
            // `order` is re-derived by reindexBlocks; the argument only seeds it.
            const newBlock = generateNewBlock(toolId, blocks.length);
            insertedId = (newBlock as { id?: string }).id ?? null;

            const next = [...blocks];
            // resolveInsertionIndex steps past a table's linked chart/text
            // companions, so inserting "after a table" never lands between the
            // table and its interpretation.
            next.splice(resolveInsertionIndex(blocks, target), 0, newBlock);

            return { tests: { ...currentTests, blocks: reindexBlocks(next) } };
        });

        if (insertedId) scrollToBlock(insertedId);

        // Returned so a caller that has something to hand the new block — a
        // dropped image file, say — can address it.
        return insertedId as string | null;
    }, []);

    /** Best-effort fingerprint write so a later Cmd+V can trust the internal payload. */
    const writeFingerprintToSystemClipboard = useCallback((text: string) => {
        navigator.clipboard?.writeText(text).catch(() => {});
    }, []);

    const copy = useCallback(
        (ids?: string[]) => {
            const result = buildClipboard('copy', ids);
            if (!result) return;
            writeFingerprintToSystemClipboard(result.serialized.text);
            toast.success(
                `${pluralize(result.payload.blocks.length, 'bloco copiado', 'blocos copiados')}. Escolha onde colar.`,
            );
        },
        [buildClipboard, writeFingerprintToSystemClipboard],
    );

    const cut = useCallback(
        (ids?: string[]) => {
            const result = buildClipboard('cut', ids);
            if (!result) return;
            writeFingerprintToSystemClipboard(result.serialized.text);
            removeBlocks(result.targetIds);
            clear();
            toast.success(
                `${pluralize(result.payload.blocks.length, 'bloco recortado', 'blocos recortados')}. Escolha onde colar.`,
            );
        },
        [buildClipboard, clear, removeBlocks, writeFingerprintToSystemClipboard],
    );

    const duplicate = useCallback(
        (ids?: string[]) => {
            const targetIds = resolveIds(ids);
            if (targetIds.length === 0) return;

            const collected = collectBlocksForClipboard(blocksRef.current, targetIds);
            if (collected.length === 0) return;

            const order = blocksRef.current.map(getBlockRealId);
            const lastRealId = [...order]
                .reverse()
                .find((id) => collected.some((block) => getBlockRealId(block) === id));

            const payload: BlockClipboardPayload = {
                version: 1,
                mode: 'copy',
                reportId: reportIdRef.current,
                blocks: collected,
            };
            applyInsert(payload, lastRealId ? { afterRealId: lastRealId } : 'end');
        },
        [applyInsert, resolveIds],
    );

    const deleteBlocks = useCallback(
        (ids?: string[]) => {
            const targetIds = resolveIds(ids);
            if (targetIds.length === 0) return;

            removeBlocks(targetIds);
            clear();
            toast.success(`${pluralize(targetIds.length, 'bloco removido', 'blocos removidos')}.`);
        },
        [clear, removeBlocks, resolveIds],
    );

    const pasteAt = useCallback(
        (target: InsertTarget) => {
            const internal = internalClipboardRef.current;
            if (!internal) return;
            applyInsert(internal.payload, target);
        },
        [applyInsert],
    );

    const getBlockClipboardFingerprint = useCallback(
        () => internalClipboardRef.current?.textFingerprint ?? null,
        [],
    );

    const dismissClipboard = useCallback(() => {
        setClipboardState(null);
    }, []);

    useBlockClipboardEvents({
        enabled,
        hasSelection: () => selectedIdsRef.current.size > 0,
        hasClipboard: () => clipboardStateRef.current !== null,
        onCopy: (event) => {
            const result = buildClipboard('copy');
            if (!result || !event.clipboardData) return false;
            event.clipboardData.setData(BLOCK_CLIPBOARD_MIME, result.serialized.json);
            event.clipboardData.setData('text/html', result.serialized.html);
            event.clipboardData.setData('text/plain', result.serialized.text);
            toast.success(
                `${pluralize(result.payload.blocks.length, 'bloco copiado', 'blocos copiados')}. Escolha onde colar.`,
            );
            return true;
        },
        onCut: (event) => {
            const result = buildClipboard('cut');
            if (!result || !event.clipboardData) return false;
            event.clipboardData.setData(BLOCK_CLIPBOARD_MIME, result.serialized.json);
            event.clipboardData.setData('text/html', result.serialized.html);
            event.clipboardData.setData('text/plain', result.serialized.text);
            removeBlocks(result.targetIds);
            clear();
            toast.success(
                `${pluralize(result.payload.blocks.length, 'bloco recortado', 'blocos recortados')}. Escolha onde colar.`,
            );
            return true;
        },
        onPaste: (payload) => {
            const order = blocksRef.current.map(getBlockRealId);
            const lastSelected = [...order].reverse().find((id) => selectedIdsRef.current.has(id));
            applyInsert(payload, lastSelected ? { afterRealId: lastSelected } : 'end');
        },
        getInternalClipboard: () => internalClipboardRef.current,
        onDeleteSelection: () => deleteBlocks(),
        onClearSelection: clear,
        onDismissClipboard: dismissClipboard,
    });

    // Plain click anywhere outside the selection chrome clears the selection
    useEffect(() => {
        if (!enabled) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (selectedIdsRef.current.size === 0) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey) return;

            const target = event.target as HTMLElement | null;
            if (target?.closest(`[${BLOCK_SELECTION_UI_ATTR}]`)) return;

            clear();
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [enabled, clear]);

    const value = useMemo<TestBlockSelectionApi>(
        () => ({
            enabled,
            selectedIds,
            isSelected,
            select,
            replaceSelection,
            clear,
            copy,
            cut,
            duplicate,
            deleteBlocks,
            pasteAt,
            moveBlocks,
            insertBlockAt,
            insertBlocksAt,
            getBlockClipboardFingerprint,
            clipboardState,
            dismissClipboard,
        }),
        [
            enabled,
            selectedIds,
            isSelected,
            select,
            replaceSelection,
            clear,
            copy,
            cut,
            duplicate,
            deleteBlocks,
            pasteAt,
            moveBlocks,
            insertBlockAt,
            insertBlocksAt,
            getBlockClipboardFingerprint,
            clipboardState,
            dismissClipboard,
        ],
    );

    return (
        <TestBlockSelectionContext.Provider value={value}>
            {children}
        </TestBlockSelectionContext.Provider>
    );
}
